'use client';

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Filter, ChevronRight, Clock, ArrowDownRight, ArrowUpRight, Loader2, ChevronDown, Calendar as CalendarIcon, RotateCcw } from 'lucide-react';
import { formatCurrency } from '@/utils/format';
import { isPaymentTransaction } from '@/utils/billingCycles';

interface RawTransaction {
  id: string;
  name?: string;
  merchantName?: string | null;
  amount: number;
  date: string;
  pending?: boolean;
  category?: string[] | null;
  custom_category?: string | null;
  paymentChannel?: string | null;
  creditCardId?: string | null;
  authorizedDate?: string | null;
  notes?: string | null;
  plaidItem?: {
    institutionName?: string;
  };
}

export interface TransactionCatalogContext {
  origin: 'billing-cycle' | 'analytics-card';
  cardId?: string;
  cardName?: string;
  cardMask?: string;
  statementLabel?: string;
  accentColor?: string;
  dateRange: {
    startISO: string;
    endISO: string;
    label: string;
  };
  meta?: {
    cycleId?: string;
    totalSpend?: number;
  };
}

interface FilterState {
  regular: boolean;
  refunds: boolean;
  payments: boolean;
  pending: boolean;
}

const defaultFilters: FilterState = {
  regular: true,
  refunds: true,
  payments: false,
  pending: false,
};

interface TransactionWithKind extends RawTransaction {
  kind: keyof FilterState;
}

interface TransactionCatalogModalProps {
  isOpen: boolean;
  context: TransactionCatalogContext | null;
  onClose: () => void;
}

function formatDisplayDate(value: string): string {
  try {
    const date = new Date(value);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  } catch {
    return value;
  }
}

function getClearedDate(transaction: RawTransaction): string {
  // Use authorizedDate when available (this is the auth/posted date)
  // Leave blank if no authorized date (pending transactions)
  if (transaction.authorizedDate) {
    return formatDisplayDate(transaction.authorizedDate);
  }
  return '';
}

function formatCategoryName(category: string): string {
  if (!category) return 'Other';

  // Handle Plaid's uppercase categories (e.g., FOOD_AND_DRINK -> Food & Drink)
  let formatted = category
    .replace(/_/g, ' ')  // Replace underscores with spaces
    .toLowerCase()        // Convert to lowercase
    .split(' ')          // Split into words
    .map(word => {
      // Capitalize first letter of each word
      if (word === 'and' || word === 'or') return '&';
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');

  // Handle specific category mappings
  const categoryMappings: { [key: string]: string } = {
    'General Merchandise': 'General',
    'Transfer Out': 'Transfers',
    'Cash Advance': 'Cash Advance',
    'Balance Transfer': 'Transfers'
  };

  // Apply category mappings
  for (const [original, replacement] of Object.entries(categoryMappings)) {
    if (formatted.toLowerCase() === original.toLowerCase()) {
      formatted = replacement;
      break;
    }
  }

  // Limit to 20 characters
  if (formatted.length > 20) {
    formatted = formatted.substring(0, 17) + '...';
  }

  return formatted;
}

function describeCategory(transaction: RawTransaction): string {
  // Use custom category if set by user
  if (transaction.custom_category) {
    return formatCategoryName(transaction.custom_category);
  }

  if (transaction.pending) {
    return 'Pending authorization';
  }
  if (isPaymentTransaction(transaction.name || '')) {
    return 'Payment or balance transfer';
  }
  if (transaction.amount < 0) {
    return 'Refund or return';
  }
  if (transaction.category && transaction.category.length > 0) {
    // Join multiple categories or return first meaningful category
    const category = Array.isArray(transaction.category) ? transaction.category[0] : transaction.category;
    // Skip single letter categories that don't make sense
    if (category && category.length > 1) {
      return formatCategoryName(category);
    }
  }
  return 'Purchase';
}

function getTransactionKind(transaction: RawTransaction): keyof FilterState {
  if (transaction.pending) {
    return 'pending';
  }
  if (isPaymentTransaction(transaction.name || '')) {
    return 'payments';
  }
  if (transaction.amount < 0) {
    return 'refunds';
  }
  return 'regular';
}

const filterDefinitions: Array<{
  key: keyof FilterState;
  label: string;
  tone: string;
  order: number;
}> = [
  { key: 'payments', label: 'Payments', tone: 'from-pink-500/90 to-rose-500/90', order: 1 },
  { key: 'refunds', label: 'Refunds', tone: 'from-emerald-500/90 to-teal-500/90', order: 2 },
  { key: 'pending', label: 'Pending', tone: 'from-slate-500/90 to-gray-500/90', order: 3 },
  { key: 'regular', label: 'Spending', tone: 'from-blue-500/90 to-indigo-500/90', order: 4 },
];

// Raw category options that will be formatted consistently
const rawCategoryOptions = [
  'FOOD_AND_DRINK',
  'SHOPS',
  'TRANSPORTATION',
  'TRAVEL',
  'ENTERTAINMENT',
  'SERVICE',
  'HEALTHCARE',
  'HOME_IMPROVEMENT',
  'GENERAL_MERCHANDISE',
  'GOVERNMENT_AND_NON_PROFIT',
  'PAYMENT_TRANSFER',
  'Other'
];

// Format category options using the same logic as Analytics
const categoryOptions = rawCategoryOptions.map(formatCategoryName);

function formatRangeLabel(start: Date, end: Date): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 'Custom range';
  }
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const startLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' })
  }).format(startDate);
  const endLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(endDate);
  return `${startLabel} – ${endLabel}`;
}

function renderSectionHeader(key: keyof FilterState, transactions: TransactionWithKind[]): JSX.Element {
  const total = transactions.reduce((sum, txn) => sum + txn.amount, 0);
  const label = filterDefinitions.find(def => def.key === key)?.label ?? key;
  const isPositive = total >= 0;
  return (
    <div className="flex items-center justify-between py-2.5 px-4 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl mb-2 border border-gray-200/50 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-900">{label}</span>
        <span className="text-xs text-gray-500 bg-white/70 px-2 py-0.5 rounded-full">
          {transactions.length}
        </span>
      </div>
      <span className={`text-sm font-bold ${isPositive ? 'text-gray-900' : 'text-green-600'}`}>
        {formatCurrency(total)}
      </span>
    </div>
  );
}

export function TransactionCatalogModal({ isOpen, context, onClose }: TransactionCatalogModalProps) {
  const [mounted, setMounted] = useState(false);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [transactions, setTransactions] = useState<TransactionWithKind[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [updatingTransactions, setUpdatingTransactions] = useState<Set<string>>(new Set());
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [customRangeActive, setCustomRangeActive] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !context) return;
    setFilters(defaultFilters);
  }, [isOpen, context]);

  useEffect(() => {
    if (!isOpen || !context) {
      return;
    }
    const start = context.dateRange.startISO ? context.dateRange.startISO.slice(0, 10) : '';
    const end = context.dateRange.endISO ? context.dateRange.endISO.slice(0, 10) : '';
    setStartDateInput(start);
    setEndDateInput(end);
    setCustomRangeActive(false);
    setDateError(null);
  }, [isOpen, context]);

  useEffect(() => {
    if (!customRangeActive) {
      setDateError(null);
      return;
    }
    if (!startDateInput || !endDateInput) {
      setDateError('Select both a start and end date.');
      return;
    }
    const start = new Date(`${startDateInput}T00:00:00`);
    const end = new Date(`${endDateInput}T23:59:59`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setDateError('Enter valid dates.');
      return;
    }
    if (start > end) {
      setDateError('Start date must be before end date.');
      return;
    }
    setDateError(null);
  }, [customRangeActive, startDateInput, endDateInput]);

  const isCustomApplied = useMemo(() => {
    if (!customRangeActive) return false;
    if (dateError) return false;
    return Boolean(startDateInput && endDateInput);
  }, [customRangeActive, dateError, startDateInput, endDateInput]);

  const effectiveStartISO = useMemo(() => {
    if (isCustomApplied && startDateInput) {
      return new Date(`${startDateInput}T00:00:00`).toISOString();
    }
    return context?.dateRange.startISO || null;
  }, [isCustomApplied, startDateInput, context]);

  const effectiveEndISO = useMemo(() => {
    if (isCustomApplied && endDateInput) {
      return new Date(`${endDateInput}T23:59:59.999`).toISOString();
    }
    return context?.dateRange.endISO || null;
  }, [isCustomApplied, endDateInput, context]);

  const dateRangeLabel = useMemo(() => {
    if (isCustomApplied && startDateInput && endDateInput) {
      return formatRangeLabel(new Date(`${startDateInput}T00:00:00`), new Date(`${endDateInput}T00:00:00`));
    }
    if (context?.dateRange.label) {
      return context.dateRange.label;
    }
    if (context?.dateRange.startISO && context?.dateRange.endISO) {
      return formatRangeLabel(new Date(context.dateRange.startISO), new Date(context.dateRange.endISO));
    }
    return 'Selected range';
  }, [isCustomApplied, startDateInput, endDateInput, context]);

  const resetRangeToContext = () => {
    if (!context) return;
    const start = context.dateRange.startISO ? context.dateRange.startISO.slice(0, 10) : '';
    const end = context.dateRange.endISO ? context.dateRange.endISO.slice(0, 10) : '';
    setStartDateInput(start);
    setEndDateInput(end);
    setCustomRangeActive(false);
    setDateError(null);
  };

  useEffect(() => {
    if (!isOpen || !context) return;

    let cancelled = false;

    const fetchTransactions = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        if (!effectiveStartISO || !effectiveEndISO) {
          setTransactions([]);
          return;
        }
        params.set('startDate', effectiveStartISO);
        params.set('endDate', effectiveEndISO);
        params.set('limit', '2000');
        if (context.cardId) {
          params.set('cardId', context.cardId);
        }

        const response = await fetch(`/api/user/transactions?${params.toString()}`);
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const data = await response.json();
        const raw: RawTransaction[] = data.transactions || [];
        if (cancelled) return;

        const enriched: TransactionWithKind[] = raw.map(txn => ({
          ...txn,
          kind: getTransactionKind(txn)
        }));
        setTransactions(enriched);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load transactions');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (!dateError) {
      fetchTransactions();
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, context, effectiveStartISO, effectiveEndISO, dateError]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [isOpen, onClose]);

  const grouped = useMemo(() => {
    const initial: Record<keyof FilterState, TransactionWithKind[]> = {
      regular: [],
      refunds: [],
      payments: [],
      pending: [],
    };
    return transactions.reduce((acc, txn) => {
      acc[txn.kind].push(txn);
      return acc;
    }, initial);
  }, [transactions]);

  const visibleTransactions = useMemo(() => {
    return Object.entries(grouped).flatMap(([key, list]) => {
      if (!filters[key as keyof FilterState]) return [] as TransactionWithKind[];
      return list;
    });
  }, [grouped, filters]);

  const totalVisible = useMemo(() => {
    return visibleTransactions.reduce((sum, txn) => sum + txn.amount, 0);
  }, [visibleTransactions]);

  const updateTransactionCategory = async (transactionId: string, newCategory: string) => {
    setUpdatingTransactions(prev => new Set(prev).add(transactionId));

    try {
      const response = await fetch(`/api/user/transactions/${transactionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ custom_category: newCategory })
      });

      if (!response.ok) {
        throw new Error('Failed to update category');
      }

      // Optimistically update the local state
      setTransactions(prev => prev.map(txn =>
        txn.id === transactionId
          ? { ...txn, custom_category: newCategory }
          : txn
      ));
    } catch (error) {
      console.error('Error updating category:', error);
      // Here you could show a toast notification about the error
    } finally {
      setUpdatingTransactions(prev => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
      setEditingCategory(null);
    }
  };

  if (!mounted) {
    return null;
  }

  if (!isOpen || !context) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true"></div>

      <div className="relative w-full sm:w-11/12 lg:w-3/4 xl:w-2/3 max-w-4xl mx-4 bg-white rounded-2xl shadow-xl border border-gray-100/50 flex flex-col max-h-[85vh] backdrop-blur-sm">
        {/* Compact Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {context.cardName || 'Transactions'}
            </h2>
            {context.cardMask && (
              <span className="text-sm text-gray-500">•••• {context.cardMask}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{dateRangeLabel}</span>
            <button
              onClick={onClose}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Close transactions"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Sleek Filters */}
        <div className="px-4 py-3 border-b border-gray-100/70 flex-shrink-0 bg-gradient-to-r from-gray-50/50 to-white">
          <div className="flex flex-wrap items-center gap-2">
            {filterDefinitions.map(def => {
              const isActive = filters[def.key];
              const badgeCount = grouped[def.key].length;
              return (
                <button
                  key={def.key}
                  onClick={() => setFilters(prev => ({ ...prev, [def.key]: !prev[def.key] }))}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25 scale-105'
                      : 'bg-white/80 text-gray-700 hover:bg-white hover:shadow-md border border-gray-200/50 hover:scale-105'
                  }`}
                  type="button"
                >
                  <span>{def.label}</span>
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {badgeCount}
                  </span>
                </button>
              );
            })}
            <div className="ml-auto text-sm text-gray-500 bg-white/60 px-3 py-1.5 rounded-full border border-gray-200/50">
              {formatCurrency(totalVisible)} • {visibleTransactions.length} transactions
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <div className="group relative">
                <div className="flex items-center gap-3 bg-gradient-to-r from-white to-gray-50/50 border border-gray-200/80 px-4 py-2.5 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 hover:border-gray-300/80">
                  <CalendarIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</label>
                    <input
                      type="date"
                      value={startDateInput}
                      onChange={(event) => {
                        setStartDateInput(event.target.value);
                        setCustomRangeActive(true);
                      }}
                      className="border-none bg-transparent text-gray-800 text-sm font-semibold focus:outline-none focus:ring-0 p-0 mt-0.5 min-w-[120px]"
                    />
                  </div>
                </div>
              </div>

              <div className="hidden sm:block text-gray-400">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>

              <div className="group relative">
                <div className="flex items-center gap-3 bg-gradient-to-r from-white to-gray-50/50 border border-gray-200/80 px-4 py-2.5 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 hover:border-gray-300/80">
                  <CalendarIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</label>
                    <input
                      type="date"
                      value={endDateInput}
                      onChange={(event) => {
                        setEndDateInput(event.target.value);
                        setCustomRangeActive(true);
                      }}
                      className="border-none bg-transparent text-gray-800 text-sm font-semibold focus:outline-none focus:ring-0 p-0 mt-0.5 min-w-[120px]"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isCustomApplied && (
                <button
                  type="button"
                  onClick={resetRangeToContext}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-gray-50 to-white border border-gray-200/80 text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 hover:border-gray-300/80 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset to Statement
                </button>
              )}
              {dateError && (
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-red-50 to-red-100/50 border border-red-200/80 text-sm font-medium text-red-700 shadow-sm">
                  <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  {dateError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                  <span className="text-sm text-gray-600">Loading transactions...</span>
                </div>
              </div>
            )}

            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            {!loading && !error && visibleTransactions.length === 0 && (
              <div className="text-center py-8">
                <Filter className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-600 font-medium">No transactions match your filters</p>
                <p className="text-gray-500 text-sm mt-1">Adjust the filters above to see more transactions.</p>
              </div>
            )}

            {/* Column Headers */}
            {!loading && !error && visibleTransactions.length > 0 && (
              <div className="grid grid-cols-12 gap-2 py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200 bg-gray-50/50 rounded-lg">
                <div className="col-span-2">Transaction Date</div>
                <div className="col-span-2">Auth Date</div>
                <div className="col-span-4">Vendor</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-2 text-right">Cost</div>
              </div>
            )}

            {filterDefinitions.sort((a, b) => a.order - b.order).map(def => {
              const list = grouped[def.key];
              if (!filters[def.key] || list.length === 0) {
                return null;
              }
              return (
                <div key={def.key}>
                  {renderSectionHeader(def.key, list)}
                  <div className="space-y-0">
                    {list.map(txn => {
                      const displayName = txn.merchantName || txn.name || 'Transaction';
                      const amount = formatCurrency(txn.amount);
                      const isNegative = txn.amount < 0;
                      const isUpdating = updatingTransactions.has(txn.id);
                      const isEditing = editingCategory === txn.id;

                      return (
                        <div
                          key={txn.id}
                          className="grid grid-cols-12 gap-2 py-1.5 px-3 text-sm hover:bg-gray-50/70 transition-colors border-b border-gray-50 last:border-b-0"
                        >
                          {/* Transaction Date - Col 1-2 */}
                          <div className="col-span-2 text-xs text-gray-500 font-medium">
                            {formatDisplayDate(txn.date)}
                          </div>

                          {/* Cleared Date - Col 3-4 */}
                          <div className="col-span-2 text-xs text-gray-500 font-medium">
                            {getClearedDate(txn)}
                          </div>

                          {/* Vendor Name - Col 5-8 */}
                          <div className="col-span-4 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {displayName}
                            </p>
                          </div>

                          {/* Category - Col 9-10 */}
                          <div className="col-span-2 min-w-0">
                            {isEditing ? (
                              <div className="relative">
                                <select
                                  className="w-full text-xs bg-white border border-blue-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                                  value={describeCategory(txn)}
                                  onChange={(e) => updateTransactionCategory(txn.id, e.target.value)}
                                  onBlur={() => setEditingCategory(null)}
                                  autoFocus
                                >
                                  {categoryOptions.map(option => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ) : (
                              <button
                                onClick={() => setEditingCategory(txn.id)}
                                className="w-full text-left group flex items-center gap-1.5 text-xs text-gray-600 hover:text-blue-600 transition-colors"
                                disabled={isUpdating}
                              >
                                <span className="truncate">
                                  {isUpdating ? 'Updating...' : describeCategory(txn)}
                                </span>
                                <ChevronDown className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                              </button>
                            )}
                          </div>

                          {/* Amount - Col 11-12 */}
                          <div className="col-span-2 text-right">
                            <p className={`font-semibold ${isNegative ? 'text-green-600' : 'text-gray-900'}`}>
                              {amount}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
